import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { DownloadAttachmentModalComponent } from '@app/admin/manage/modals/download-attachment-modal/download-attachment-modal.component';
import { FileType } from '@app/enums/FileTypes';
import { MedpaceUploadComponent } from '@components/atoms/medpace-upload/medpace-upload.component';
import {
    MdsChipComponent,
    MdsDatepickerComponent,
    MdsDropdownComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { Expense } from '@models/request';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { isTypeMileageExpense } from '@utility/utility';
import { filter, switchMap, take, tap } from 'rxjs';
import { BaseExpenseComponent } from '../base-expense-edit.component';
import { OtherExpenseFormGroup } from './other-expense-edit.form';

@Component({
    selector: 'medpace-other-expense-edit',
    imports: [
        MdsDropdownComponent,
        MedpaceUploadComponent,
        AsyncPipe,
        MdsTextFieldComponent,
        MdsDatepickerComponent,
        MdsChipComponent,
    ],
    templateUrl: './other-expense-edit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class OtherExpenseEditComponent extends BaseExpenseComponent<OtherExpenseFormGroup> {
    adminRequestService = inject(AdminRequestServices);
    dialog = inject(MatDialog);
    public readonly acceptableFileTypes: string[] = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];

    file$ = this.expenseFormGroup$.pipe(switchMap((formGrp) => formGrp.controls.file.valueChangesReplay));

    override push(expense: Expense): Expense {
        super.push(expense);

        expense.manualTotalCalculation = undefined;
        expense.mileageComments = undefined;
        expense.mileageDistanceUnits = undefined;
        expense.mileageStartLocation = undefined;
        expense.mileageEndLocation = undefined;
        expense.mileageReimbursementCountry = undefined;
        expense.mileageReimbursementRatePerUnit = undefined;
        expense.mileageTotalDistance = undefined;

        return expense;
    }

    public getDownloadExpenseAttachmentFn() {
        return (attachmentId: number) => {
            this.requestNotNull$
                .pipe(
                    take(1),
                    tap((request) => {
                        const attachmentUri = this.reimbursementRequestService.getAttachmentUri({
                            requestId: request.id,
                            attachmentId,
                        });
                        const patient = this.patient();
                        this.dialog.open(DownloadAttachmentModalComponent, {
                            data: {
                                dataSourceUrl: attachmentUri.toString(),
                                attachmentName: `${patient.patientIdentification.firstName}-${patient.patientIdentification.lastName}-expense-attachment`,
                            },
                            autoFocus: false,
                        });
                    })
                )
                .subscribe();
        };
    }
    onFileUploaded(file: File) {
        this.expenseFormGroup().controls.file.setValue(file);
        this.expenseFormGroup().controls.file.markAsDirty(); // programmatic setValue does not mark as dirty
    }
    patchValue$ = this.expenseNotNull$
        .pipe(
            filter((expense) => !isTypeMileageExpense(expense.expenseId)),
            tap((expense) => {
                this.patchFormGroup(expense);
            }),
            takeUntilDestroyed()
        )
        .subscribe();
}
